import React, { useState, useEffect } from 'react';
import { 
  Building, Users, Landmark, ShoppingBag, 
  Briefcase, GraduationCap, HeartPulse, Truck,
  CheckCircle, ArrowRight, ShieldCheck, Zap, Menu, X, Activity, Smartphone
} from 'lucide-react';
import logo from '../assest/logo.png';
import '../App.css';

const Solutions = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-[Poppins] antialiased text-slate-800 bg-white selection:bg-emerald-100 selection:text-emerald-900 overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-[100] bg-white h-14 border-b border-slate-100 shadow-sm">
        <div className="max-w-[1240px] mx-auto h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="w-8 h-8">
               <img src={logo} alt="HRMS Software" className="w-full h-full object-contain" />
             </div>
             <span className="text-base font-extrabold tracking-tight text-slate-800">HRMS<span className="text-emerald-500 font-black">Software</span></span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-slate-500 font-semibold text-[13px]">
            <a href="/" className="hover:text-emerald-500 transition-all hover:scale-105">Home</a>
            <a href="/features" className="hover:text-emerald-500 transition-all hover:scale-105">Features</a>
            <a href="/about" className="hover:text-emerald-500 transition-all hover:scale-105">About Us</a>
            <a href="/solutions" className="text-emerald-500 transition-all hover:scale-105 font-bold border-b-2 border-emerald-500">Solutions</a>
            <a 
              href="/SecureInsights.apk" 
              download="SecureInsights.apk"
              className="px-6 py-2 rounded-[6px] bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition-all flex items-center gap-2 active:scale-95 shadow-[0_10px_30px_rgba(16,185,129,0.25)]"
            >
               Download App
            </a>
          </nav>

          <button className="md:hidden p-2 text-slate-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-white border-b border-slate-100 shadow-xl z-[150] py-6 px-6 md:hidden">
            <nav className="flex flex-col gap-5 text-slate-600 font-bold text-sm">
              <a href="/" onClick={() => setIsMenuOpen(false)}>Home</a>
              <a href="/features" onClick={() => setIsMenuOpen(false)}>Features</a>
              <a href="/about" onClick={() => setIsMenuOpen(false)}>About Us</a>
              <a href="/solutions" onClick={() => setIsMenuOpen(false)}>Solutions</a>
              <a 
                href="/SecureInsights.apk" 
                download="SecureInsights.apk"
                className="w-full py-4 rounded-[6px] bg-emerald-600 text-white shadow-lg text-center"
              >
                Download App
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-center pt-20 pb-8 overflow-hidden bg-[#070b14]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none opacity-30"></div>
        <div className="max-w-[1100px] mx-auto px-6 text-center space-y-4 relative z-10 w-full">
           <div className="inline-flex items-center gap-2.5 px-3 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-2 font-bold text-[9px] tracking-[0.2em] text-emerald-400 uppercase mx-auto">
              <Activity size={10} className="text-emerald-500" />
              Vertical Expertise
           </div>
           <h1 className="text-2xl md:text-5xl font-extrabold leading-tight tracking-tighter text-white">
             One Ecosystem. <br />
             <span className="text-emerald-500">Every Industry.</span>
           </h1>
           <p className="text-xs md:text-sm text-slate-400 font-medium max-w-xl mx-auto leading-relaxed opacity-90">
             HRMS Software is engineered to adapt. Whether you're managing a corporate powerhouse or a logistics fleet, our tools scale with your ambition.
           </p>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
           <SolutionCard icon={Landmark} title="Corporate & Banking" desc="High-security attendance management with detailed audit trails and encrypted payroll processing." />
           <SolutionCard icon={ShoppingBag} title="Retail & E-commerce" desc="Manage shifts across multiple locations with real-time reporting and automatic overtime calculation." />
           <SolutionCard icon={Truck} title="Logistics & Fleet" desc="Track field agents with precise GPS geofencing and mobile-native clocking features." />
           <SolutionCard icon={HeartPulse} title="Healthcare Systems" desc="Ensure 24/7 staffing compliance with intelligent shift rotation and leave management." />
           <SolutionCard icon={GraduationCap} title="Education & Institutes" desc="Simplify faculty attendance and payroll with integrated cloud-based reporting." />
           <SolutionCard icon={Building} title="Manufacturing Units" desc="Handle high-density workforce logistics with robust terminal sync and daily reporting." />
        </div>
      </section>

      {/* Why Enterprise Choose Us */}
      <section className="pb-20 bg-white">
         <div className="max-w-[1100px] mx-auto px-6">
            <div className="bg-[#070b14] rounded-[24px] p-8 lg:p-16 relative overflow-hidden shadow-2xl">
               <div className="absolute top-0 right-0 w-72 h-72 bg-emerald-500/10 blur-[80px] rounded-full translate-x-1/3 -translate-y-1/3"></div>
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
                  <div className="space-y-6">
                     <h3 className="text-2xl md:text-3xl font-extrabold text-white leading-tight tracking-tight">
                       Engineered for <br />
                       <span className="text-emerald-500 font-extrabold">Absolute Reliability.</span>
                     </h3>
                     <div className="space-y-4">
                        <BenefitItem title="Unmatched Security" desc="Data isolation and end-to-end encryption ensure your organizational secrets stay secret." />
                        <BenefitItem title="API First" desc="Integrate HRMS Software into your existing toolkit with our robust RESTful architecture." />
                     </div>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-[20px] space-y-6">
                     <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-500/20 text-emerald-500 rounded-lg flex items-center justify-center">
                           <ShieldCheck size={22} />
                        </div>
                        <h4 className="text-lg font-extrabold text-white uppercase tracking-wider">Enterprise Tier</h4>
                     </div>
                     <p className="text-xs text-slate-400 font-medium leading-relaxed">Ready to scale your organization with the most secure HRMS on the market?</p>
                     <button className="w-full py-4 bg-white text-slate-900 rounded-[10px] font-black text-xs uppercase tracking-widest hover:bg-emerald-500 hover:text-white transition-all shadow-xl">
                        Talk to an Expert
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#070b14] py-16 border-t border-white/5">
         <div className="max-w-[1240px] mx-auto px-6 flex flex-col items-center justify-center text-center gap-10">
            <div className="flex items-center gap-3">
               <div className="w-10 h-10 rounded-xl bg-emerald-500/10 p-2 border border-emerald-500/20">
                  <img src={logo} alt="Logo" className="w-full h-full object-contain filter brightness-0 invert" />
               </div>
               <span className="text-xl font-black text-white tracking-tight uppercase tracking-widest">HRMS SOFTWARE</span>
            </div>
            
            <p className="text-[10px] md:text-[11px] font-bold text-slate-500 uppercase tracking-[0.4em] max-w-[280px] md:max-w-none leading-relaxed">
               © 2026 HRMS SOFTWARE — ALL RIGHTS RESERVED
            </p>
            
            <div className="flex items-center gap-8 md:gap-12 text-[11px] font-black text-slate-400">
               <a href="/" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">Home</a>
               <a href="/about" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">About Us</a>
               <a href="/features" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">Software</a>
               <a href="/solutions" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110 text-emerald-500">Solutions</a>
            </div>
         </div>
      </footer>
    </div>
  );
};

const SolutionCard = ({ icon: Icon, title, desc }) => (
  <div className="bg-white p-8 rounded-[20px] border border-slate-100 shadow-sm hover:shadow-md transition-all group">
     <div className="w-12 h-12 bg-slate-50 text-slate-900 rounded-[16px] flex items-center justify-center mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-all">
        <Icon size={24} strokeWidth={2.5} />
     </div>
     <h4 className="text-lg font-extrabold text-slate-900 mb-3 tracking-tight">{title}</h4>
     <p className="text-[13px] text-slate-500 leading-relaxed font-medium opacity-80">{desc}</p>
  </div>
);

const BenefitItem = ({ title, desc }) => (
   <div className="flex gap-4">
      <div className="w-5 h-5 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center shrink-0 mt-1">
         <CheckCircle size={12} strokeWidth={4} />
      </div>
      <div className="space-y-1">
         <h5 className="text-white font-extrabold text-[13px] uppercase tracking-wide">{title}</h5>
         <p className="text-slate-400 text-xs font-medium leading-relaxed opacity-80">{desc}</p>
      </div>
   </div>
);

export default Solutions;
